import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgRedux } from '@angular-redux/store';
import { IAppState, AppAction } from '../../../store';
import { APP_CONFIG } from '../../../api/config';
import { ActionHandler } from '../../../store/action.handler';
import { IAppConfig } from '../../../store';
import { Title } from '@angular/platform-browser';
import { Reservation } from '../../../api/model/reservation/reservation.model';
import { ModifyReservationDelegate } from '../../../api/delegates/modify-reservation.delegate';
import { SiteURL, SiteURLUtils } from '../../../common/site.url.utils';
import { IMessage } from '../../../api/interfaces/Messages/IMessage';
import { ReservationErrorMessageKeys } from '../../../api/model/errors/reservation-error-message-keys.model';
import { useDefault } from '../../../common/common';
import { Hotel } from '../../../api/model/hotel/hotel.model';
import { MediaQueryService } from '../../../common/media-query/media-query.service';
import { ReservationStayPreferences } from '../../../api/model/reservation/stay-preferences/reservation-stay-preferences.model';
import { MessagesUtil } from '../../../common/messages.util';
import { RBFInteractionService } from '../../../api/services/rbf-interaction.service';
import { TrackingJsonService } from '../../../api/services/trackingJson.service';
import { RouteNames } from '../../../api/enums/route-names.enum';
import { User } from '../../../api/model/user/user.model';
import { BadRequestError, NotFoundError } from '../../../api/model/errors/service-error.model';
import { RESERVATION_STATUS_CANCELED } from '../../../api/model/reservation/reservation-status.model';
import { Unsubscribe } from 'redux';
import { ApplicationRouteNames } from '../../../common/model/application-route-names.model';
import { OverrideReservationGuestError } from '../../../api/model/errors/reservation-error.model';
import { ViewScrollService } from '../../../api/services/view-scroll.service';
import { PageSectionType } from '../../../common/model/page/page-section.model';
import { MessageCategory } from '../../../api/model/message/message-category.model';

const ROUTE_TITLE = 'IHG | Change Your Reservation';
const RESERVATION_NUMBER_PARAM_NAME = 'id';
const LAST_NAME_PARAM_NAME = 'lastName';

@Component({
  selector: 'stay-mgmt-modify-res',
  templateUrl: './modify-res.component.html',
  styleUrls: ['./modify-res.component.scss']
})
export class ModifyResComponent implements OnInit, OnDestroy {
  AppAction = AppAction;
  state: IAppState;
  reservation: Reservation;
  hotel: Hotel;
  reservationId: string;
  lastName: string;
  translations: { [key: string]: string };
  error: any = null;
  message: IMessage = null;
  actionInProgress: boolean;
  availableReservation: Reservation;
  isExtraSmallViewPort: boolean;
  stayPreferences: ReservationStayPreferences;
  isStayPreferenceUpdate: boolean;
  isRateSelection: boolean;
  member: User;
  firstCheckForChangesCompleted: boolean;
  showChangeReservation:boolean = true;
  messages: Map<MessageCategory, IMessage>;

  unsubscribe: Unsubscribe;

  constructor(
    @Inject(APP_CONFIG) protected appConfig: IAppConfig,
    private _ngRedux: NgRedux<IAppState>,
    private _activatedRoute: ActivatedRoute,
    private _titleService: Title,
    private _rbfInteractionService: RBFInteractionService,
    private _modifyReservationDelegate: ModifyReservationDelegate,
    private _trackingJson: TrackingJsonService,
    private _router: Router,
    private _mediaQueryService: MediaQueryService,
    private _viewScrollService: ViewScrollService) {
    this._titleService.setTitle(ROUTE_TITLE);
  }

  ngOnInit() {
    this.reservation = null;
    this.availableReservation = null;
    this.stayPreferences = null;
    this.hotel = null;
    this.actionInProgress = true;
    this.member = null;

    this.firstCheckForChangesCompleted = false;
    this.messages = new Map<MessageCategory, IMessage>();

    this._trackingJson.subscribeRoute(RouteNames.ModifyReservation);

    this.updateFromState();
    this.unsubscribe = this._ngRedux.subscribe(() => {
      this.updateFromState();
    });
    this._activatedRoute.params.subscribe(params => {
      this.reservationId = params[RESERVATION_NUMBER_PARAM_NAME];
      this.lastName = params[LAST_NAME_PARAM_NAME];
      if (this.reservation === null) {
        this._modifyReservationDelegate.fetchReservation(this.reservationId, this.lastName);
      } else {
        this._postReservationBehavior();
      }
    });
    if (this._rbfInteractionService.isFromFindHotels({}, this.reservationId, this.lastName)) {
      this.isRateSelection = true;
      this._rbfInteractionService.resetFindHotelsInteraction(this.reservationId, this.lastName, ApplicationRouteNames.CHANGE_RESERVATION);
    }
    this._activatedRoute.queryParams.subscribe(queryParams => {
      if (this._rbfInteractionService.isFromStayPreferences(queryParams)) {
        this.isStayPreferenceUpdate = true;
        this.message = MessagesUtil.toSuccessMessage(ReservationErrorMessageKeys.SUCCESS_GLOBAL_STAY_PREFERENCES);
        this._rbfInteractionService.resetStayPreferencesInteraction(this.reservationId, this.lastName, ApplicationRouteNames.CHANGE_RESERVATION);
      }
    });
    this.isExtraSmallViewPort = this._mediaQueryService.isExtraSmallViewPort();
    this._rbfInteractionService.init();
    this._modifyReservationDelegate.fetchMember();
  }

  ngOnDestroy() {
    this._trackingJson.unsubscribeRoute(RouteNames.ModifyReservation);
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  displayMessage(messageDetails) {
    this.messages.set(messageDetails.category, messageDetails.message);
    if (!!messageDetails.message) {
      this._viewScrollService.scrollToSection(PageSectionType.PAGE_MESSAGING_MODULE);
    }
  }

  getMessageKeys() {
    return this.messages.keys();
  }

  updateFromState() {
    this.state = this._ngRedux.getState();
    if (!this.state) {
      return;
    }
    this.availableReservation = useDefault(this.state.availableReservation, null);
    this.translations = MessagesUtil.filterTranslations(this.state.translations, 'modifyreservation');
    this.reservation = useDefault(this.state.availableReservation, this.state.reservation);
    this.member = this.state.member;
    ActionHandler.handleError(this.state, AppAction.GET_RESERVATION, () => {
      const error = this.state.error;
      if (error instanceof NotFoundError) {
        SiteURLUtils.redirectToSiteUrl(this.appConfig, SiteURL.CONTENT_NOT_FOUND);
      } else if (error instanceof BadRequestError) {
        SiteURLUtils.redirectToSiteUrl(this.appConfig, SiteURL.HOMEPAGE);
      } else {
        this.error = error;
        this.actionInProgress = false;
        this.message = MessagesUtil.fromError(this.appConfig, error, ReservationErrorMessageKeys.RETRIEVE_ERROR_UNKNOWN_KEY);
      }
    });
    ActionHandler.handleError(this.state, AppAction.GET_HOTEL, () => {
      this.error = this.state.error;
      this.actionInProgress = false;
      this.message = MessagesUtil.fromError(this.appConfig, this.state.error, ReservationErrorMessageKeys.RETRIEVE_ERROR_UNKNOWN_KEY);
    });
    ActionHandler.handleSuccess(this.state, AppAction.GET_RESERVATION, () => {
      this._postReservationBehavior();
    });
    ActionHandler.handleSuccess(this.state, AppAction.GET_STAY_PREFERENCES, () => {
      this.stayPreferences = this.state.stayPreferences;
    });
    ActionHandler.handleSuccess(this.state, AppAction.GET_HOTEL, () => {
      this.hotel = this.state.hotel;
      this.showChangeReservation =  this.reservation && this.hotel && this.reservation.rate && !(this.hotel.isInGreaterChina() && !this.reservation.rate.refundable);
      if (!this.showChangeReservation){
        const param = SiteURLUtils.getLocalizedReservationViewUrl(this.appConfig, this.reservation.confirmationNumber, this.reservation.guest.lastName);
        this.message = MessagesUtil.toErrorMessage(ReservationErrorMessageKeys.ERROR_GREATERCHINA, [param], true);
      }
      this._updatePageLoadInProgress();
    });
    ActionHandler.handleSuccess(this.state, AppAction.CHECK_AVAILABILITY, () => {
      if (this.isRateSelection && this.availableReservation !== null) {
        this.message = MessagesUtil.toSuccessMessage(ReservationErrorMessageKeys.SUCCESS_GLOBAL_AVAILABILITY);
      }
      this.firstCheckForChangesCompleted = true;
      this._updatePageLoadInProgress();
    });
    ActionHandler.handleError(this.state, AppAction.CHECK_AVAILABILITY, () => {
      if (this.firstCheckForChangesCompleted) {
        return;
      }
      this.error = this.state.error;
      this.message = MessagesUtil.fromError(this.appConfig, this.error, ReservationErrorMessageKeys.AVAILABILITY_ERROR_UNKNOWN_KEY);
      this.firstCheckForChangesCompleted = true;
      this._updatePageLoadInProgress();
    });
    ActionHandler.handleInitial(this.state, AppAction.MODIFY_RESERVATION, () => {
      this.error = null;
      this.actionInProgress = true;
    });
    ActionHandler.handleError(this.state, AppAction.MODIFY_RESERVATION, () => {
      this.error = this.state.error;
      this.actionInProgress = false;
      this.message = MessagesUtil.fromError(this.appConfig, this.error, ReservationErrorMessageKeys.MODIFY_ERROR_UNKNOWN_KEY);
    });
    ActionHandler.handleSuccess(this.state, AppAction.MODIFY_RESERVATION, () => {
      this._handleUpdatedReservation();
      SiteURLUtils.redirectToReservationChangeConfirmation(this.appConfig, this.reservation.confirmationNumber, this.reservation.guest.lastName);
    });
    ActionHandler.handleSuccess(this.state, AppAction.CLEAR_CHANGES, () => {
      this._modifyReservationDelegate.checkReservationAvailability();
    });
    ActionHandler.handleSuccess(this.state, AppAction.LOAD_CHANGES, () => {
      this._modifyReservationDelegate.checkReservationAvailability();
    });
    ActionHandler.handleError(this.state, AppAction.LOAD_CHANGES, () => {
      this.firstCheckForChangesCompleted = true;
      this._updatePageLoadInProgress();
    });
    ActionHandler.handleSuccess(this.state, AppAction.CHANGE_MEMBER_STATUS, () => {
      this.error = null;
      this._modifyReservationDelegate.checkReservationAvailability();
    });
    ActionHandler.handleSuccess(this.state, AppAction.GET_MEMBER, () => {
      this.member = this.state.member;
      this._updatePageLoadInProgress();
    });
    ActionHandler.handleError(this.state, AppAction.GET_MEMBER, () => {
      this._updatePageLoadInProgress();
    });
    ActionHandler.handleSuccess(this.state, AppAction.CLEAR_ALL_CHANGES, () => {
      this._rbfInteractionService.goToPreviousPage(this.appConfig, this.reservation.confirmationNumber, this.reservation.guest.lastName);
    });
    ActionHandler.handleSuccess(this.state, AppAction.ASSOCIATE_RESERVATION, () => {
      this._handleUpdatedReservation();
    });
    this._handleUserMessages();
  }

  private _postReservationBehavior() {
    if (!!this.state.reservation && (this.state.reservation.status === RESERVATION_STATUS_CANCELED)) {
      this._router.navigate([ApplicationRouteNames.CANCEL_CONFIRMATION, this.reservationId, this.lastName], { replaceUrl: true })
      return;
    }
    this._updatePageLoadInProgress();
    if (this.hotel === null && (!!this.reservation && !!this.reservation.hotelMnemonic)) {
      this._modifyReservationDelegate.fetchHotel(this.reservation.hotelMnemonic);
    }
    if (this.availableReservation === null) {
      this._modifyReservationDelegate.fetchSessionReservationChanges(this.reservation);
    }
    if (this.stayPreferences === null) {
      this._modifyReservationDelegate.fetchStayPreferences(this.reservation);
    }
  }

  private _updatePageLoadInProgress() {
    this.actionInProgress = this.error === null && (this.hotel === null || this.reservation === null || !this.firstCheckForChangesCompleted);
  }

  private _handleUpdatedReservation() {
    if (this.lastName !== this.reservation.guest.lastName) {
      this._rbfInteractionService.resetPayInteraction(this.reservation.confirmationNumber, this.reservation.guest.lastName, ApplicationRouteNames.CHANGE_RESERVATION);
    }
  }
  private _handleUserMessages() {
    this.messages.set(MessageCategory.RESERVATION_AVAILABILITY, null);
    if (!this.reservation || !this.member || !!this.message) {
      return;
    }
    const signIn = SiteURLUtils.getLocalizedSiteUrl(this.appConfig, SiteURL.SIGN_IN);
    if (this.member.isExplicit() && (!!this.reservation.guest.rewardsClubNumber && this.member.rewardsClubNumber !== this.reservation.guest.rewardsClubNumber)) {
      this.messages.set(MessageCategory.RESERVATION_AVAILABILITY, MessagesUtil.toErrorMessage(ReservationErrorMessageKeys.ERROR_ACCESS, [signIn], true));
    } else if (!this.member.isExplicit() && this._requiresExplicitUser()) {
      this.messages.set(MessageCategory.RESERVATION_AVAILABILITY, MessagesUtil.toErrorMessage(ReservationErrorMessageKeys.ERROR_AUTHORIZATION, [signIn], true));
    }
  }
  private _requiresExplicitUser() {
    return this.reservation.rate.isRewardNight;
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/routes/modify-res/modify-res/modify-res.component.ts